import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import { StatusCollectionView, type StatusCollectionCheck } from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import { useStatusInventoryAccess } from '@/lib/useStatusInventoryAccess';
import { getStatusManagedServiceSummary, listStatusConfigPage } from '@/lib/status-inventory';

export default function StatusCollectionPage() {
  const { activeTenant } = useScope();
  const { navigate } = useNavigation();
  const access = useStatusInventoryAccess();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [sort, setSort] = useState<{ id: string; desc: boolean }[]>([]);
  const [page, setPage] = useState(0);
  useEffect(() => { const timer = setTimeout(() => setQuery(search), 250); return () => clearTimeout(timer); }, [search]);
  useEffect(() => { setSearch(''); setQuery(''); setState(''); setSort([]); setPage(0); }, [activeTenant?.id]);
  const enabled = activeTenant?.id && access.accessEnabled;
  const checks = useSWR(enabled ? ['status-collection', activeTenant.id, query, state, page, sort] : null,
    () => listStatusConfigPage<StatusCollectionCheck>('collection-checks', { page: page + 1, pageSize: 50, q: query, sort: sort.map(item => `${item.desc ? '-' : ''}${item.id}`).join(',') || undefined, filters: state ? { status_group: state } : {} }),
    { refreshInterval: 30_000, refreshWhenHidden: false });
  const summary = useSWR(enabled ? ['status-inventory-managed-service', activeTenant.id] : null, getStatusManagedServiceSummary,
    { refreshInterval: 30_000, refreshWhenHidden: false });
  const onDevice = useCallback((id: string) => navigate(`/status/inventory/devices?device_id=${encodeURIComponent(id)}`), [navigate]);
  if (!enabled) return <div role="status" className="p-6">Select a tenant with Status inventory access.</div>;
  const counts = summary.data;
  return <StatusCollectionView checks={checks.data?.items ?? []} total={checks.data?.total ?? 0} page={page} search={search} state={state}
    sort={sort} onSort={value => { setSort(value); setPage(0); }} loading={checks.isLoading} error={checks.error?.message ?? summary.error?.message}
    summary={counts ? { healthy: counts.healthy_module_count, attention: counts.unhealthy_module_count - counts.stale_check_count,
      stale: counts.stale_check_count, awaiting: counts.awaiting_module_count, expected: counts.expected_check_count, affectedDevices: counts.affected_device_count } : undefined}
    onSearch={value => { setSearch(value); setPage(0); }} onState={value => { setState(value); setPage(0); }} onPage={setPage}
    onRefresh={() => { void checks.mutate(); void summary.mutate(); }} onDevice={onDevice} />;
}
